import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    Tabs,
    Tab,
    CircularProgress
} from '@mui/material'
import { useState } from 'react'

const GET_USER_VOUCHERS = gql`
  query GetUserVouchers($userId: ID!) {
    user(id: $userId) {
      id
      first_name
      last_name
      voucher_created_by {
        id
        reference_number
        title
        description
        total_amount
        state
        created_at
        department {
          name
        }
      }
      voucher_pay_to {
        id
        reference_number
        title
        description
        total_amount
        state
        created_at
        department {
          name
        }
      }
      voucher_claimed_by {
        id
        reference_number
        title
        description
        total_amount
        state
        created_at
        department {
          name
        }
      }
    }
  }
`

interface UserVouchersProps {
    userId: string
    userName: string
    open: boolean
    onClose: () => void
}

interface Voucher {
    id: string
    reference_number: number
    title: string
    description: string
    total_amount: number
    state: string
    created_at: string
    department: {
        name: string
    }
}

const UserVouchers: React.FC<UserVouchersProps> = ({ userId, userName, open, onClose }) => {
    const [activeTab, setActiveTab] = useState(0)
    const { data, loading, error } = useQuery(GET_USER_VOUCHERS, {
        variables: { userId },
        skip: !open
    })

    const user = data?.user
    const createdVouchers: Voucher[] = user?.voucher_created_by || []
    const payToVouchers: Voucher[] = user?.voucher_pay_to || []
    const claimedVouchers: Voucher[] = user?.voucher_claimed_by || []

    const getStateColor = (state: string) => {
        const stateColors: { [key: string]: string } = {
            DRAFT: 'default',
            SUBMITTED: 'primary',
            PAID: 'success',
            REJECTED: 'error',
            ARCHIVED: 'warning'
        }
        return stateColors[state] || 'default'
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString()
    }

    const renderVouchersTable = (vouchers: Voucher[]) => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Created</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vouchers.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                No vouchers found
                            </TableCell>
                        </TableRow>
                    ) : (
                        vouchers.map((voucher) => (
                            <TableRow key={voucher.id}>
                                <TableCell>{voucher.reference_number}</TableCell>
                                <TableCell>{voucher.title || '-'}</TableCell>
                                <TableCell>{voucher.description}</TableCell>
                                <TableCell>${voucher.total_amount?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={voucher.state}
                                        color={getStateColor(voucher.state) as any}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{voucher.department?.name || '-'}</TableCell>
                                <TableCell>{formatDate(voucher.created_at)}</TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Vouchers for {userName}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">
                        Error loading vouchers: {error.message}
                    </Typography>
                ) : (
                    <Box>
                        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                            <Tab label={`Created (${createdVouchers.length})`} />
                            <Tab label={`Pay To (${payToVouchers.length})`} />
                            <Tab label={`Claimed (${claimedVouchers.length})`} />
                        </Tabs>

                        {activeTab === 0 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Vouchers Created by User
                                </Typography>
                                {renderVouchersTable(createdVouchers)}
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Vouchers Paid To User
                                </Typography>
                                {renderVouchersTable(payToVouchers)}
                            </Box>
                        )}

                        {activeTab === 2 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Vouchers Claimed by User
                                </Typography>
                                {renderVouchersTable(claimedVouchers)}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

export default UserVouchers